import React from 'react';

const Why: React.FC = () => (
  <main className="main-content page-enter">
    <section className="why-section">
      <h2 className="tag-line">Warum "For The Boys"?</h2>
      <article className="why-article">

        <p>
          Es gibt in der Schweiz Organisationen, die sich mit Männerproblemen beschäftigen. Es gibt
          Parteien, die sich als Stimme der Männer inszenieren. Und doch: Männer in echten
          Krisensituationen — suizidgefährdet, obdachlos, süchtig, aus dem Bildungssystem gefallen —
          fühlen sich von keiner von ihnen wirklich angesprochen. Das ist kein Zufall. Es ist ein
          systematisches Versagen auf zwei Seiten des politischen Spektrums.
        </p>

        <h3>männer.ch: Das Richtige falsch erklärt</h3>

        <p>
          männer.ch ist die bekannteste Männerorganisation der Schweiz. Sie macht Öffentlichkeitsarbeit,
          bildet Fachpersonen aus und hat das Thema Männergesundheit jahrelang in den Diskurs
          eingebracht. Das verdient Respekt. Aber ihr Erklärungsmodell für männliche Probleme ist
          ideologisch verengt — und das hat direkte Konsequenzen dafür, wen sie erreichen und wen nicht.
        </p>

        <p>
          Markus Theunert, Gründungsdirektor von männer.ch, formuliert die Kernthese seiner Organisation
          unmissverständlich:{' '}
          <em>
            „Männer kommen als Menschen auf die Welt und werden erst dann zu Männern gemacht."
          </em>{' '}
          Und:{' '}
          <em>„Männlich-Sein ist erlernt."</em>{' '}
          Daraus folgt die gesamte politische Logik: Da Männlichkeit ein soziales Konstrukt ist,
          entstehen auch die Probleme von Männern durch falsche Normen und überholte Rollenbilder —
          ein „Korsett" (Theunerts eigenes Wort), das aufgebrochen werden muss. Die Lösung lautet
          dementsprechend stets: Bewusstsein schärfen, Normen dekonstruieren, Männer zur inneren
          „Befreiung" einladen.
        </p>

        <p>
          Das klingt fortschrittlich. Es hat jedoch einen entscheidenden Haken:{' '}
          <strong>Diese Erklärung stimmt nur zum Teil.</strong>
        </p>

        <p>
          Wenn Männer in der Schweiz 73 % der Suizide begehen, wenn sie 75–80 % der Obdachlosen
          stellen, wenn Knaben bei identischen Testergebnissen systematisch schlechtere Zeugnisnoten
          erhalten — dann reicht das „Korsett der Männlichkeit" als Erklärung schlicht nicht aus.
          Neurobiologische und evolutionspsychologische Forschung zeigt klar: Männer reagieren
          biologisch anders auf chronischen Stress (HPA-Achse, Cortisolprofil), weisen eine
          Testosteron-bedingte höhere Risikobereitschaft auf und bilden im Schnitt aktivitätsbasierte
          statt emotional-verbale soziale Netzwerke — ein Muster, das sich kulturübergreifend
          nachweisen lässt und damit nicht durch Sozialisation erklärt werden kann.
          Diese Befunde sind in peer-reviewten Studien dokumentiert. Sie zu ignorieren ist keine
          Neutralität — es ist selektive Wissenschaft im Dienst einer Ideologie.
        </p>

        <p>
          männer.ch zieht an dieser Stelle eine Grenze, die intellektuell problematisch ist. Wer
          biologische Erklärungsansätze einbezieht oder fragt, ob Testosteron, Neurobiologie oder
          Evolutionspsychologie eine Rolle spielen, wird von männer.ch in die Nähe von
          Online-Radikalisierung gerückt — so der Titel ihrer Leitkampagne:{' '}
          <strong>„Vorsicht Manosphere!"</strong>. Unter diesem Label werden seriöse
          wissenschaftliche Positionen und tatsächlich radikale Online-Bewegungen in einen Topf
          geworfen. Das ist eine rhetorische Immunisierungsstrategie, keine argumentative
          Auseinandersetzung.
        </p>

        <p>
          Ein konkretes Beispiel macht die Schieflage sichtbar: männer.ch organisiert Kampagnen, in
          denen Männer Röcke tragen, um gegen starre Geschlechternormen zu protestieren. Das mag als
          Kunstform legitim sein. Aber ein 14-jähriger Schulabbrecher in der Pubertät, ein 42-jähriger
          Mann, der nach der Kündigung die Wohnung verloren hat, ein 67-jähriger Pensionierter ohne
          ein einziges enges soziales Netzwerk — diese Männer werden von einem Röcke-Protest nicht
          erreicht. Nicht im Entferntesten. Das ist kein böswilliger Vorwurf, sondern eine
          nüchterne Feststellung über Zielgruppen.
        </p>

        <p>
          Erschwerend kommt hinzu: männer.ch betreibt ausdrücklich Bündnispolitik mit feministischen
          Organisationen und definiert sich selbst als Teil der Gleichstellungsbewegung. Das ist ein
          legitimer politischer Entscheid. Er bedeutet aber, dass berechtigte Kritik an Teilen des
          feministischen Diskurses — etwa die Frage, ob jahrzehntelange einseitige Bildungsförderung
          die Bubenkrise mitverursacht hat — strukturell nicht formuliert werden kann, ohne die
          eigenen Kooperationspartner zu brüskieren. Das Ergebnis ist institutionalisierte
          Selbstzensur. Und genau diese Lücke macht männer.ch für Männer, die echte Antworten
          suchen, zunehmend unglaubwürdig.
        </p>

        <h3>Die Rechte: Ausländer als Ablenkung</h3>

        <p>
          Auf der anderen Seite des Spektrums tauchen Männer als politische Kategorie fast
          ausschliesslich im Kontext von Migration und Kriminalität auf. Zur Ehrlichkeit gehört:
          Ausländerkriminalität ist ein ernst zu nehmendes empirisches Thema — und verdient
          eine saubere, datenbasierte Analyse statt politischer Vereinnahmung in die eine oder
          andere Richtung.
        </p>

        <p>
          Genau das leistet der Schweizer Forensikpsychiater Frank Urbaniok in seinem Buch
          {' '}<em>Schattenseiten der Migration: Zahlen, Fakten, Lösungen</em> (2025). Urbaniok —
          ehemaliger Chefarzt des Psychiatrisch-Psychologischen Dienstes des Kantons Zürich —
          bereinigt die Schweizer Kriminalstatistik systematisch um sozioökonomische Faktoren:
          Alter, Geschlecht, Bildung, Einkommen. Das Ergebnis ist zweigeteilt. Erstens: Ein
          grosser Teil der rohen Überrepräsentation von Ausländern in der PKS erklärt sich
          tatsächlich durch strukturelle Ungleichheiten — Migranten sind im Schnitt jünger,
          häufiger männlich und wirtschaftlich schlechter gestellt. Soweit bestätigt die Forschung
          das soziologische Standardargument. Zweitens — und das ist der unbequeme Befund: Selbst
          nach vollständiger Kontrolle aller sozioökonomischen Faktoren bleibt bei bestimmten
          Herkunftsgruppen, darunter Teile Nordafrikas, eine signifikante Übervertretung bei
          schweren Gewalt- und Sexualdelikten bestehen. Das ist kein politisches Argument — es ist
          ein empirischer Befund, der eine ehrliche Debatte verlangt, keine Verdrängung.
        </p>

        <p>
          Was sie aber nicht ersetzt: Sozialpolitik für Männer. Ob der Mann der nachts auf der
          Strasse schläft, der Knabe der in der Schule verloren geht oder der Vater der nach der
          Scheidung zusammenbricht — es ist ein Mensch der Hilfe braucht. Herkunft ist dabei
          irrelevant. Und für genau diese Menschen hat die Rechte keine Antwort. Männergesundheit,
          Vaterschaftsurlaub, Suizidprävention, Housing First — das sind systemische Fragen, die
          systemische Antworten verlangen. Wer das verweigert und stattdessen einen Sündenbock
          anbietet, sagt dem Mann in der Krise im Grunde: Du bist nicht schuld. Aber geholfen
          wird dir trotzdem nicht.
        </p>

        <h3>Was "For The Boys" anders macht</h3>

        <p>
          For The Boys ist weder links noch rechts. Es ist das, was fehlt: eine rationale,
          evidenzbasierte Männerpolitik, die biologische Realitäten anerkennt, ohne in Ressentiments
          zu verfallen, und die Systemfehler benennt, ohne auf Sündenböcke zu zeigen.
        </p>

        <div className="why-pillars">
          <div className="why-pillar">
            <h4>Biologie ernst nehmen</h4>
            <p>
              Männer reagieren anders auf Stress, sind biologisch stärker suchtgefährdet bei
              bestimmten Substanzen, bilden andere soziale Netzwerke. Das ist keine Ideologie —
              es ist dokumentierte Wissenschaft, die in der Politikgestaltung endlich
              berücksichtigt werden muss.
            </p>
          </div>
          <div className="why-pillar">
            <h4>Strukturen kritisieren, nicht Geschlechter</h4>
            <p>
              Die Bildungspolitik hat Knaben strukturell vernachlässigt. Das Gesundheitssystem
              kennt keine Männermedizin. Das Schweizer Sozialrecht kennt bis heute keine gleichwertige Elternzeit für Väter — mit dem Effekt, dass Männer strukturell in die Versorgerrolle gedrängt werden, während die Betreuungsarbeit bei Müttern verbleibt.
              Das sind Systemfehler — keine Angriffe auf Frauen oder Feministinnen.
            </p>
          </div>
          <div className="why-pillar">
            <h4>Männer in Krisen ansprechen</h4>
            <p>
              Obdachlosigkeit, Suizid, Spielsucht, Schulabbruch — das sind die Themen, die zählen.
              Nicht Bewusstseinsworkshops für gut situierte Männer, die sich nach mehr
              Gefühlstiefe sehnen.
            </p>
          </div>
          <div className="why-pillar">
            <h4>Fakten statt Lager</h4>
            <p>
              Wir zitieren das BFS, die SUVA, das Obsan und internationale Peer-Review-Studien.
              Wir nehmen Kritik ernst, auch wenn sie unbequem ist — auch Kritik am feministischen
              Mainstream, sofern sie sachlich begründet ist.
            </p>
          </div>
        </div>

        <p>
          Markus Theunert hat recht, wenn er sagt, dass viele Männer sich selbst fremd geworden
          sind. Aber er irrt, wenn er glaubt, dass dieser Käfig ausschliesslich aus gelernten
          Normen besteht. Teile des Käfigs sind biologisch. Andere sind strukturell. Und einige
          wurden von einer Sozialpolitik gebaut, die Männer systematisch übersehen hat — während
          sie gleichzeitig behauptete, für sie zu sprechen.
        </p>

        <p className="why-closing">
          Diese Website existiert, weil das aufhören muss.
        </p>

      </article>
    </section>
  </main>
);

export default Why;
